import { ReactNode, useMemo, useRef } from 'react';
import { useCreditPack, useUniqueId } from 'src/hooks';
import {
  DrawerTooltipContainer,
  DrawerTooltipContainerRef,
} from 'src/v2/commonComponents/DrawerTooltipContainer';
import { PackOptionsToggle } from '../PackOptionsToggle';
import styles from './PackOptionsTooltip.module.scss';
import { SVG_SIZE_S } from 'src/constants';
import { PackOption } from '../PackOption';
import { Button } from 'src/v2/commonComponents/Button';
import { Check } from '@phosphor-icons/react';

interface PackOptionsTooltipProps {
  autoPayAmount?: number;
  setAutoPayAmount: (value: number) => void;
}

export const PackOptionsTooltip = ({
  autoPayAmount,
  setAutoPayAmount,
}: PackOptionsTooltipProps) => {
  const drawerRef = useRef<DrawerTooltipContainerRef>(null);
  const { uniqueId } = useUniqueId();
  const tooltipAnchorId = useMemo(
    () => `pack-options-tooltip-${uniqueId}`,
    [uniqueId],
  );

  const { formattedPacks } = useCreditPack();

  const { value: selectedValue, credit: selectedCredit } = useMemo(() => {
    return (
      formattedPacks.find(({ value }) => value === autoPayAmount) ??
      formattedPacks[0]
    );
  }, [autoPayAmount, formattedPacks]);

  const handlePackOptionClick = (value: number) => {
    setAutoPayAmount(value);
    drawerRef?.current?.onVisibilityToggle();
  };

  return (
    <DrawerTooltipContainer<{
      title?: ReactNode;
    }>
      ref={drawerRef}
      id={tooltipAnchorId}
      place="bottom-start"
      ToggleButton={PackOptionsToggle}
      toggleButtonProps={{
        title: <PackOption value={selectedValue} credit={selectedCredit} />,
      }}
      onlyTooltip
      classNameTooltip={styles.tooltip}
    >
      <div className={styles.container}>
        {formattedPacks.map(({ value, credit }) => (
          <Button
            color="transparent"
            key={value}
            fullWidth
            className={styles.packOptionButton}
            onClick={() => handlePackOptionClick(value)}
          >
            <PackOption value={value} credit={credit} />

            {value === selectedValue ? (
              <Check size={SVG_SIZE_S} />
            ) : (
              <span className={styles.mock} />
            )}
          </Button>
        ))}
      </div>
    </DrawerTooltipContainer>
  );
};
